/**
 * index.js — Application entry point.
 *
 * Bootstraps the Apollo GraphQL client pointed at the Shopify Storefront API,
 * wraps the app in ApolloProvider, and mounts it into the DOM.
 * Also runs the localStorage version-check before rendering.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React from 'react';
import ReactDOM from 'react-dom';
import { ApolloClient } from 'apollo-client';
import { createHttpLink } from 'apollo-link-http';
import { setContext } from 'apollo-link-context';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { ApolloProvider } from 'react-apollo';

// ─── Swiper styles ────────────────────────────────────────────────────────────
import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';
import 'swiper/components/navigation/navigation.min.css';
import 'swiper/components/pagination/pagination.min.css';

// ─── Internal ─────────────────────────────────────────────────────────────────
import './index.css';
import App from './App';
import Maintenance from './components/Maintenance/Maintenance';
import reportWebVitals from './reportWebVitals';
import { checkAndResetLocalStorage } from './components/versionControl';

// ─── Apollo / Shopify config ──────────────────────────────────────────────────

/**
 * HTTP link pointing at the live Shopify Storefront GraphQL endpoint.
 * TODO: move credentials to environment variables (.env) to avoid committing them.
 */


//Staging store — uncomment and swap httpLink to use during development
// const httpLink = createHttpLink({ uri: 'https://leo-dev-staging.myshopify.com/api/2025-01/graphql.json' });
// const middlewareLink = setContext(() => ({
//   headers: {
//     'X-Shopify-Storefront-Access-Token': '63e618d04a41cba1cd57f9003d86f703'
//   }
// }))


/** Injects the Shopify Storefront access token into every request header. */
const httpLink = createHttpLink({ uri: 'https://demo-leo.myshopify.com/api/2025-01/graphql.json' });
const middlewareLink = setContext(() => ({
  headers: {
    'X-Shopify-Storefront-Access-Token': '00d7b69c131a8156b860225217feeb8b'
  }
}));

const client = new ApolloClient({
  link: middlewareLink.concat(httpLink),
  cache: new InMemoryCache(),
});

// ─── Pre-render checks ────────────────────────────────────────────────────────

/** Clears stale localStorage data if the app version has changed. */
checkAndResetLocalStorage();

// ─── Maintenance mode ─────────────────────────────────────────────────────────
/**
 * Flip MAINTENANCE_MODE to true to show the maintenance page to every visitor.
 *
 * Developer bypass: visit  /?preview=<MAINTENANCE_BYPASS_KEY>  once (any page).
 * That stores a flag in localStorage so every later visit loads the real site,
 * even while maintenance is on for the public. Visit  /?preview=exit  to drop it.
 */
const MAINTENANCE_MODE = false;
const MAINTENANCE_BYPASS_KEY = 'leo-preview-2026';

function isMaintenanceBypassed() {
  try {
    const preview = new URLSearchParams(window.location.search).get('preview');
    if (preview === MAINTENANCE_BYPASS_KEY) {
      localStorage.setItem('maintenance_bypass', 'true');
    } else if (preview === 'exit') {
      localStorage.removeItem('maintenance_bypass');
    }
    return localStorage.getItem('maintenance_bypass') === 'true';
  } catch (e) {
    return false; // localStorage unavailable → show maintenance
  }
}

const showMaintenance = MAINTENANCE_MODE && !isMaintenanceBypassed();

/**
 * Take over scroll restoration from the browser as early as possible (before
 * React mounts), so a refresh or direct load never restores the previous scroll
 * position (which made pages appear to load at the bottom/footer). ScrollToTop
 * then keeps every route starting at the top.
 */
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

// ─── Mount ────────────────────────────────────────────────────────────────────

ReactDOM.render(
  showMaintenance ? (
    <Maintenance />
  ) : (
    <ApolloProvider client={client}>
      <App />
    </ApolloProvider>
  ),
  document.getElementById('root')
);

// https://bit.ly/CRA-vitals
reportWebVitals();
